import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, type AvatarShape } from "../../components/Avatar.js";
import { Icon } from "../../components/Icon.js";
import { useUser } from "../../auth/UserContext.js";
import { fmt } from "../../lib/money.js";
import ActionModal, { type ActionModalState } from "./ActionModal.js";
import BillsModal from "./BillsModal.js";
import { approvePendingTransfer, rejectPendingTransfer } from "../../api/pendingTransfers.js";
import { sendFamilySummary } from "../../api/statements.js";
import type { ParentBundle } from "../../api/me.js";

export default function Dashboard() {
  const { bundle, refresh } = useUser();
  const navigate = useNavigate();
  const [modal, setModal] = useState<ActionModalState | null>(null);
  const [billsKid, setBillsKid] = useState<{ id: string; name: string } | null>(null);
  const [processingKey, setProcessingKey] = useState<string | null>(null);
  const [summarySending, setSummarySending] = useState(false);
  const [summaryLabel, setSummaryLabel] = useState<"send" | "sent" | "failed">("send");

  const handleSendSummary = async () => {
    if (summarySending) return;
    setSummarySending(true);
    try {
      await sendFamilySummary();
      setSummaryLabel("sent");
      setTimeout(() => setSummaryLabel("send"), 3000);
    } catch {
      setSummaryLabel("failed");
      setTimeout(() => setSummaryLabel("send"), 3000);
    } finally {
      setSummarySending(false);
    }
  };

  if (!bundle || bundle.user.role !== "parent") return null;
  const parent = bundle as ParentBundle;
  const family = parent.family;
  const pendingTransfers = parent.pendingTransfers ?? [];

  const total = family.reduce((s, k) => s + k.account.balanceCents, 0);
  const totalAllowance = family.reduce((s, k) => s + k.account.allowanceCents, 0);
  const ages = family.map((k) => k.user.age ?? 0).filter((a) => a > 0);
  const minAge = ages.length ? Math.min(...ages) : 0;
  const maxAge = ages.length ? Math.max(...ages) : 0;

  const findKid = (kidId: string) => family.find((k) => k.user.rowKey === kidId);

  const handleApprove = async (rowKey: string) => {
    setProcessingKey(rowKey);
    try {
      await approvePendingTransfer(rowKey);
      await refresh();
    } finally {
      setProcessingKey(null);
    }
  };

  const handleReject = async (rowKey: string) => {
    setProcessingKey(rowKey);
    try {
      await rejectPendingTransfer(rowKey);
      await refresh();
    } finally {
      setProcessingKey(null);
    }
  };

  return (
    <div>
      <div className="row-between" style={{ marginBottom: 24 }}>
        <div>
          <h1>Family dashboard</h1>
          <p className="soft" style={{ marginTop: 6 }}>
            All accounts at a glance.
          </p>
        </div>
        <div className="row" style={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
          <button
            className="btn btn-ghost"
            onClick={handleSendSummary}
            disabled={summarySending}
            title="Email a 30-day activity summary to yourself"
          >
            <Icon name="mail" size={16} />
            {summaryLabel === "sent"
              ? "Sent!"
              : summaryLabel === "failed"
                ? "Failed"
                : "Email summary"}
          </button>
          <button className="btn btn-ghost" onClick={() => setModal({ type: "transfer" })}>
            <Icon name="transfer" size={16} /> Transfer
          </button>
          <button className="btn btn-primary" onClick={() => setModal({ type: "add" })}>
            <Icon name="plus" size={16} /> Add funds
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-3" style={{ marginBottom: 24 }}>
        <div className="card">
          <div className="soft" style={{ fontSize: 13, marginBottom: 4 }}>
            Total across all accounts
          </div>
          <div className="money money-lg">{fmt(total)}</div>
        </div>
        <div className="card">
          <div className="soft" style={{ fontSize: 13, marginBottom: 4 }}>
            Monthly allowance paid
          </div>
          <div className="money money-lg">{fmt(totalAllowance)}</div>
          <div className="soft" style={{ fontSize: 12, marginTop: 4 }}>
            Next: 1st of next month
          </div>
        </div>
        <div className="card">
          <div className="soft" style={{ fontSize: 13, marginBottom: 4 }}>
            Kids
          </div>
          <div className="money money-lg">{family.length}</div>
          {ages.length > 0 && (
            <div className="soft" style={{ fontSize: 12, marginTop: 4 }}>
              Ages {minAge}–{maxAge}
            </div>
          )}
        </div>
      </div>

      {/* Kids grid */}
      <h3 style={{ marginBottom: 14 }}>Accounts</h3>
      <div className="grid grid-3">
        {family.map((k) => {
          const color = k.user.color ?? "var(--brand)";
          const recent = (k.recentTxns ?? [])[0];
          const avatarShape = (k.user.avatarShape as AvatarShape) ?? "circle";
          return (
            <div
              key={k.user.rowKey}
              className="card"
              style={{ padding: 22, position: "relative", overflow: "hidden" }}
            >
              <div
                className="decoration"
                style={{
                  position: "absolute",
                  top: -30,
                  right: -30,
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  background: color,
                  opacity: 0.1,
                }}
              />

              <div className="row" style={{ marginBottom: 18, position: "relative" }}>
                <Avatar kid={{ name: k.user.name, color, avatarShape }} size={48} />
                <div>
                  <div style={{ fontWeight: 500, fontSize: 17 }}>{k.user.name}</div>
                  <div className="soft" style={{ fontSize: 12 }}>
                    Age {k.user.age ?? "—"}
                  </div>
                </div>
              </div>

              <div className="money money-lg" style={{ marginBottom: 4 }}>
                {fmt(k.account.balanceCents)}
              </div>
              <div className="soft" style={{ fontSize: 12, marginBottom: 14 }}>
                Allowance {fmt(k.account.allowanceCents)}/mo
              </div>

              {recent && (
                <div
                  style={{
                    padding: "10px 12px",
                    background: "var(--canvas-warm)",
                    borderRadius: 10,
                    fontSize: 12,
                    marginBottom: 14,
                  }}
                >
                  <div className="soft">Last activity</div>
                  <div className="row-between">
                    <div>{recent.note}</div>
                    <div
                      className={recent.amountCents > 0 ? "money-pos" : "money-neg"}
                      style={{ fontWeight: 500 }}
                    >
                      {fmt(recent.amountCents, { showSign: true })}
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                <button
                  className="btn btn-soft btn-sm"
                  onClick={() => setModal({ type: "add", kidId: k.user.rowKey })}
                >
                  <Icon name="plus" size={14} /> Add
                </button>
                <button
                  className="btn btn-soft btn-sm"
                  onClick={() => setModal({ type: "subtract", kidId: k.user.rowKey })}
                >
                  <Icon name="minus" size={14} /> Subtract
                </button>
                <button
                  className="btn btn-soft btn-sm"
                  onClick={() => setModal({ type: "transfer", kidId: k.user.rowKey })}
                >
                  <Icon name="transfer" size={14} /> Send
                </button>
                <button
                  className="btn btn-soft btn-sm"
                  onClick={() => setBillsKid({ id: k.user.rowKey, name: k.user.name })}
                >
                  <Icon name="minus" size={14} /> Bills
                </button>
                <button
                  className="btn btn-soft btn-sm"
                  style={{ gridColumn: "1 / -1" }}
                  onClick={() => navigate(`/parent/kids/${k.user.rowKey}`)}
                >
                  <Icon name="arrow" size={14} /> View {k.user.name}&apos;s dashboard
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pending transfer requests */}
      {pendingTransfers.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <h3 style={{ marginBottom: 14 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Icon name="transfer" size={18} />
              Pending transfer requests
              <span
                style={{
                  background: "oklch(0.6 0.18 27)",
                  color: "white",
                  borderRadius: 99,
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "1px 7px",
                }}
              >
                {pendingTransfers.length}
              </span>
            </span>
          </h3>
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            {pendingTransfers.map((p, i) => {
              const fromKid = findKid(p.fromKidId);
              const toKid = findKid(p.toKidId);
              const busy = processingKey === p.rowKey;
              return (
                <div
                  key={p.rowKey}
                  style={{
                    padding: "16px 20px",
                    borderBottom:
                      i < pendingTransfers.length - 1 ? "1px solid var(--canvas-warm)" : "none",
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 12,
                  }}
                >
                  {/* From avatar */}
                  {fromKid && (
                    <Avatar
                      kid={{
                        name: fromKid.user.name,
                        color: fromKid.user.color ?? "var(--brand)",
                        avatarShape: (fromKid.user.avatarShape as AvatarShape) ?? "circle",
                      }}
                      size={36}
                    />
                  )}
                  <Icon name="arrow" size={16} color="var(--ink-mute)" />
                  {/* To avatar */}
                  {toKid && (
                    <Avatar
                      kid={{
                        name: toKid.user.name,
                        color: toKid.user.color ?? "var(--brand)",
                        avatarShape: (toKid.user.avatarShape as AvatarShape) ?? "circle",
                      }}
                      size={36}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>
                      {fromKid?.user.name ?? p.fromKidId} → {toKid?.user.name ?? p.toKidId}
                    </div>
                    <div className="soft" style={{ fontSize: 12 }}>
                      {p.note} ·{" "}
                      {new Date(p.requestedAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                    </div>
                  </div>
                  <div
                    className="money money-sm money-neg"
                    style={{ fontWeight: 600, minWidth: 64 }}
                  >
                    {fmt(p.amountCents)}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      className="btn btn-soft btn-sm"
                      style={{ color: "var(--neg)" }}
                      onClick={() => handleReject(p.rowKey)}
                      disabled={busy}
                    >
                      <Icon name="close" size={14} /> Reject
                    </button>
                    <button
                      className="btn btn-brand btn-sm"
                      onClick={() => handleApprove(p.rowKey)}
                      disabled={busy}
                    >
                      <Icon name="check" size={14} /> Approve
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {modal && (
        <ActionModal
          modal={modal}
          family={family}
          onClose={() => setModal(null)}
          onCompleted={refresh}
        />
      )}
      {billsKid && (
        <BillsModal kidId={billsKid.id} kidName={billsKid.name} onClose={() => setBillsKid(null)} />
      )}
    </div>
  );
}
